import { useTranslation } from 'react-i18next'

import {
  Button,
  Card,
  ChannelAvatar,
  Dialog,
  EmptyState,
  Flex,
  Heading,
  Link2OffIcon,
  Text,
} from '@buffer-mono/popcorn'
import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { isService } from '~publish/helpers/channels'
import { useRefreshChannelConnection } from '~publish/hooks/useRefreshChannelConnection'

import { ChannelDisplayType } from '../../types'
import type { Profile } from '../../types'

import styles from './ProfilesDisconnectedModal.module.css'

import type { JSX } from 'react'

export function ProfilesDisconnectedModal({
  disconnectedProfiles = [],
  hideModal,
  displayExtraMessage,
}: {
  disconnectedProfiles: (Profile & {
    // set via Redux, not available on Profile
    reconnecting?: boolean
  })[]
  hideModal: () => void
  displayExtraMessage?: boolean
}): JSX.Element {
  const { t } = useTranslation()
  const refreshChannelConnection = useRefreshChannelConnection()
  const canManageChannels = useHasOrganizationAction('manageChannels')

  const onReconnectClick = (profile: Profile): void => {
    if (!isService(profile.service)) return

    refreshChannelConnection({
      channel: {
        id: profile.id,
        service: profile.service,
        name: profile.formatted_username,
        apiVersion: profile.api_version || undefined,
      },
      cta: `publish-modal-channelRefresh-${profile.service.toLowerCase()}-1`,
    })
  }

  if (!disconnectedProfiles || disconnectedProfiles.length === 0) {
    return <></>
  }

  return (
    <Dialog open onOpenChange={hideModal}>
      <Dialog.Content className={styles.content}>
        <Dialog.Body className={styles.body}>
          <Dialog.CloseButton />

          <EmptyState
            size="large"
            variant="critical"
            className={styles.emptyState}
          >
            <EmptyState.Icon>
              <Link2OffIcon />
            </EmptyState.Icon>
            <Heading size="medium">
              {t('profiles-disconnected-modal.headline', {
                count: disconnectedProfiles.length,
              })}
            </Heading>
          </EmptyState>
          <Flex
            direction="column"
            align="center"
            gap="sm"
            className={styles.copyWrapper}
          >
            <Text color="subtle" align="center">
              {t('profiles-disconnected-modal.body1', {
                count: disconnectedProfiles.length,
              })}
            </Text>
            {displayExtraMessage && (
              <Text color="subtle" align="center">
                {t('profiles-disconnected-modal.extraMessage.instagram')}
              </Text>
            )}
          </Flex>

          <Flex
            gap="sm"
            direction="column"
            fullWidth
            className={styles.profileCardsWrapper}
          >
            {disconnectedProfiles.map((p) => {
              const channelDisplayType = Object.entries(
                ChannelDisplayType,
              ).find(([type]) => type === p.service_type)?.[1]

              return (
                <Card key={p.id} className={styles.profileCard}>
                  <Flex fullWidth justify="between" align="center">
                    <Flex gap="lg" align="center">
                      <ChannelAvatar
                        size="small"
                        src={
                          p.avatar_https ||
                          'https://s3.amazonaws.com/buffer-ui/Default+Avatar.png'
                        }
                        alt={p.handle}
                        channel={isService(p.service) ? p.service : 'twitter'}
                      />

                      <Flex direction="column" gap="3xs">
                        <Text>
                          <strong>{p.formatted_username}</strong>
                        </Text>
                        <Text color="subtle">{channelDisplayType}</Text>
                      </Flex>
                    </Flex>
                    {!canManageChannels ? (
                      <Text>Contact an org admin</Text>
                    ) : (
                      <Button
                        disabled={p.reconnecting}
                        onClick={(): void => onReconnectClick(p)}
                        size="large"
                      >
                        {p.reconnecting
                          ? t('profiles-disconnected-modal.reconnecting')
                          : t('profiles-disconnected-modal.cta')}
                      </Button>
                    )}
                  </Flex>
                </Card>
              )
            })}
          </Flex>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  )
}
